import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Composable
fun TournamentGroupsTab(
    token: String?,
    tournament: TournamentDto,
    grouping: TournamentGroupingDto?,
    players: List<TournamentGroupPlayerDto>,
    canEdit: Boolean,
    onReload: () -> Unit,
) {
    var dateFrom by remember(tournament.id, grouping) { mutableStateOf(grouping?.dateFrom ?: "") }
    var dateTo by remember(tournament.id, grouping) { mutableStateOf(grouping?.dateTo ?: todayIso()) }
    var threshold by remember(tournament.id, grouping) { mutableStateOf(grouping?.threshold?.toString() ?: "165") }
    var busy by remember(tournament.id) { mutableStateOf(false) }
    var error by remember(tournament.id) { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    H3 { Text("Rozdělení do skupin") }
    P(attrs = { classes("muted") }) {
        Text("Všichni hráči ze soupisek sezóny turnaje, včetně nepřihlášených. " +
            "Průměr se vypočítá za zvolené období včetně obou krajních dnů a uloží se. " +
            "Pozdější výsledky ani přihlášky rozdělení nezmění.")
    }
    if (canEdit) {
        Div {
            Label { Text("Období od: ") }
            Input(type = InputType.Date) {
                value(dateFrom); if (busy) disabled()
                onInput { dateFrom = it.value }
            }
            Label { Text(" Do: ") }
            Input(type = InputType.Date) {
                value(dateTo); if (busy) disabled()
                onInput { dateTo = it.value }
            }
        }
        Div {
            Label { Text("Mez průměru pro skupinu A: ") }
            Input(type = InputType.Text) {
                value(threshold); if (busy) disabled()
                onInput { threshold = it.value.replace(',', '.') }
            }
        }
        P(attrs = { classes("muted") }) {
            Text("Počítají se skutečně odehrané ligové hry podle data utkání a turnajové hry " +
                "započítávané do žebříčku podle data začátku turnaje, i z předchozích sezón. " +
                "Průměr ≥ mez → A, nižší průměr nebo žádné hry → B.")
        }
        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                val value = threshold.toDoubleOrNull()
                if (busy || dateFrom.isBlank() || dateTo.isBlank() || dateTo < dateFrom ||
                    value == null || !value.isFinite() || value !in 0.0..300.0) disabled()
                onClick {
                    val limit = threshold.toDoubleOrNull() ?: return@onClick
                    if (grouping != null && !window.confirm("Přepočítat rozdělení? Uložené průměry a skupiny včetně ručních změn budou nahrazeny novým výpočtem pro všechny hráče na soupisce.")) return@onClick
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            Api.computeTournamentGroups(token, tournament.id, dateFrom, dateTo, limit)
                            onReload()
                        } catch (e: Throwable) {
                            error = "Rozdělení selhalo: ${e.apiMessage()}"
                        }
                        busy = false
                    }
                }
            }) { Text(if (busy) "Počítám…" else if (grouping == null) "Vypočítat a uložit rozdělení" else "Přepočítat a nahradit rozdělení") }
        }
    }
    error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    if (grouping != null && canEdit) P(attrs = { classes("muted") }) {
        Text("Skupiny lze upravit ručně. Změna se projeví i u přihlášeného hráče, uložený průměr se nepřepočítá. Nový přepočet ruční změny nahradí.")
    }
    TournamentGroupsOverview(grouping, players, groupEditingEnabled = canEdit && !busy,
        onGroupChange = if (!canEdit) null else { playerId, group ->
            busy = true
            error = null
            scope.launch {
                try {
                    Api.setTournamentPlayerGroup(token, tournament.id, playerId, group)
                    onReload()
                } catch (e: Throwable) {
                    error = "Změna skupiny selhala: ${e.apiMessage()}"
                }
                busy = false
            }
        })
}

@Composable
fun TournamentGroupsOverview(
    grouping: TournamentGroupingDto?, players: List<TournamentGroupPlayerDto>,
    groupEditingEnabled: Boolean = false, onGroupChange: ((String, String) -> Unit)? = null,
) {
    if (grouping == null) {
        P { Text("Rozdělení do skupin zatím není připravené.") }
        return
    }
    P {
        Text("Uložené rozdělení: ${formatDate(grouping.dateFrom)} – ${formatDate(grouping.dateTo)}" +
            " · mez ${grouping.threshold} · vypočítáno ${kotlin.js.Date(grouping.computedAt).toLocaleString()}")
    }
    P(attrs = { classes("muted") }) {
        Text("Seznam zahrnuje i hráče nepřihlášené do turnaje. Průměry odpovídají okamžiku výpočtu.")
    }
    listOf("A", "B").forEach { group ->
        val members = players.filter { it.groupCode == group }
            .sortedWith(compareByDescending<TournamentGroupPlayerDto> { it.average ?: -1.0 }.thenBy { it.playerName.lowercase() })
        H4 { Text("Skupina $group (${members.size})") }
        if (members.isEmpty()) {
            P { Text("Bez hráčů.") }
        } else {
            Div(attrs = { classes("table-scroll") }) {
                Table(attrs = { classes("public-table") }) {
                    Tr {
                        Th { Text("Hráč") }; Th { Text("Tým") }
                        Th { Text("Her v období") }; Th { Text("Uložený průměr") }
                        if (onGroupChange != null) Th { Text("Skupina") }
                    }
                    members.forEach { player ->
                        Tr {
                            Td { Text(player.playerName) }; Td { Text(player.teamName) }
                            Td { Text(player.games.toString()) }
                            Td { Text(player.average?.let { it.asDynamic().toFixed(2) as String } ?: "Bez her") }
                            if (onGroupChange != null) Td {
                                Select(attrs = {
                                    attr("aria-label", "Skupina hráče ${player.playerName}")
                                    if (!groupEditingEnabled) disabled()
                                    onChange { ev -> ev.value?.let { onGroupChange(player.playerId, it) } }
                                }) {
                                    listOf("A", "B").forEach { code ->
                                        Option(code, attrs = { if (player.groupCode == code) selected() }) { Text(code) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
